import { StandardProps } from "@/types/types";
import JLDSPagination from "@JohnLewisPartnership/jl-design-system/dist/components/pagination/Pagination";
import { dispatchLegacyAnalyticsEvent } from "@/utils/analytics";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { getAnalyticsPageType } from "@/utils/setJLData";
import { useEffect, useRef } from "react";
import classNames from "classnames/bind";
import styles from "./Pagination.module.scss";
import { useFeatureFlags } from "@/flags/flags";
import { useRouteParams } from "@/hooks/useRouteParams";
import { PAGINATION } from "@/utils/events";
import { SanitisingLink } from "@/components/Pagination/utils/SanitisingLink.tsx";

const cx = classNames.bind(styles);

export function Pagination({
  productListingData,
  configProps,
}: Pick<StandardProps, "productListingData" | "configProps">) {
  const flags = useFeatureFlags();
  const { query, queryWithoutPage, pageType, facetId } = useRouteParams();

  const { results } = productListingData;

  const activePage = parseInt(
    (Array.isArray(query.page) ? query.page[0] : query.page) || "1",
  );

  const paginationRef = useRef<HTMLDivElement>(null);

  useEffect(
    function removeMisleadingListSemantics() {
      // JLDS puts every page number in one <li>, so the announced item count is wrong
      paginationRef.current
        ?.querySelectorAll("nav ul, nav li")
        .forEach((el) => el.setAttribute("role", "none"));
    },
    [activePage, results],
  );

  useEffect(
    function labelActivePage() {
      // JLDS renders the active page as a bare number with no accessible context
      const activePageEl = paginationRef.current?.querySelector(
        '[data-testid="page-active"]',
      );
      if (!activePageEl || activePageEl.hasAttribute("aria-current")) return;

      activePageEl.setAttribute("aria-current", "page");
      const prefix = document.createElement("span");
      prefix.className = cx("visuallyHidden");
      prefix.textContent = "Page ";
      activePageEl.prepend(prefix);
    },
    [activePage],
  );

  const totalPagesAvailable = Math.ceil(
    results / Number(flags.pageSize?.value),
  );

  if (totalPagesAvailable < 2) {
    return null;
  }

  const onLinkClicked = (pageNumber: number) => {
    dispatchLegacyAnalyticsEvent(PAGINATION, {
      pagetype: getAnalyticsPageType(pageType, results),
      pageNum: pageNumber,
    });
  };

  const pathname = `/${pageType}/${facetId?.join("/") || ""}`;

  return (
    <div
      data-component="Pagination"
      className={cx("pagination", {
        "pagination--desktopOnly": !!configProps?.desktopOnly,
        "pagination--centred": !!configProps?.centred,
        "pagination--right": !!configProps?.right,
      })}
      ref={paginationRef}
    >
      <JLDSPagination
        activePage={activePage}
        totalPages={totalPagesAvailable}
        onSelect={(pageNumber: number) => onLinkClicked(pageNumber)}
        location={{
          pathname,
          search: sanitiseQueryParams(queryWithoutPage),
        }}
        LinkComponent={SanitisingLink}
        ariaLabel={`Product listing page ${configProps?.desktopOnly ? "above" : "below"} grid pagination`}
        className={cx({
          "pagination--centred": !!configProps?.centred,
          "pagination--right": !!configProps?.right,
        })}
      />
    </div>
  );
}
